import React, { useCallback, ChangeEvent, FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { GeometryType } from "@evergis/api";

import { ModalDialog } from "../../DialogComponents/ModalDialog";
import { FeaturesMultiEditHeader } from "./FeaturesMultiEditHeader";
import { FeaturesMultiEditActions } from "./FeaturesMultiEditActions";
import { CalculationBlock } from "./CalculationBlock";
import { CalculationTemplates } from "./CalculationTemplates/CalculationTemplates";

import { useSelectAttribute } from "../hooks/useSelectAttribute";
import { useCreateNewAttribute } from "../hooks/useCreateNewAttribute";
import { useModeEditorToggle } from "../hooks/useModeEditorToggle";
import { useRunEditAttributes } from "../hooks/useRunEditAttributes";
import { newAttributeOptionValue } from "../hooks/useCurrentAttributesList";
import { useAdvancedEditorExpression } from "../hooks/useAdvancedEditorExpression";
import { useLayerFromDialog } from "../../../../hooks/layer/useLayerFromDialog";
import { useGeometrySelectionConditions } from "../../../../hooks/geometrySelection/useGeometrySelectionConditions";
import { getTableFilterConditions } from "../../../../ducks/table/selectors";

import { FilterConditionOperation } from "../../../LayerFilter/types";

export const FeaturesMultiEdit: FC = () => {
  const { layer, loading: loadingLayer } = useLayerFromDialog();
  const geometrySelectionConditions = useGeometrySelectionConditions();
  const tableFilterConditions = useSelector(getTableFilterConditions);

  const {
    onChangeAttribute,
    onChangeAttributeValue,
    attributeOption,
    attributeValue,
    templateValue,
    handleSetTemplateValue,
    resetAttributeValue,
  } = useSelectAttribute();

  const { onChangeAttributeName, onChangeAttributeType, newAttributeName, newAttributeType } = useCreateNewAttribute();

  const attributeInfo = {
    attributeOption,
    attributeValue,
    newAttributeName,
    newAttributeType,
    templateValue,
    resetAttributeValue,
  };

  const { advancedMode, toggleMode } = useModeEditorToggle();
  const isNewAttribute = attributeOption && attributeOption.value === newAttributeOptionValue;
  const { handleSetExpression, editorExpression } = useAdvancedEditorExpression(attributeValue);

  const clearValues = useCallback(() => {
    resetAttributeValue();
    handleSetTemplateValue(null);
  }, [resetAttributeValue, handleSetTemplateValue]);

  const resultCondition = useMemo(
    () =>
      [geometrySelectionConditions?.[layer.name], tableFilterConditions?.[layer.name], layer.condition]
        .filter(Boolean)
        .join(` ${FilterConditionOperation.AND} `),
    [geometrySelectionConditions, layer.condition, layer.name, tableFilterConditions],
  );

  const { runEditAttributesTask } = useRunEditAttributes({
    attributeInfo,
    layerName: layer.name,
    layerCondition: resultCondition,
    templateValue,
    isNewAttribute,
    advancedMode,
    editorExpression,
    clearValues,
  });

  const isNewAttributeInfo: boolean = newAttributeType?.value && newAttributeName !== null && newAttributeName !== "";
  const isAttributeInfo: boolean = attributeOption?.value;
  const isAttributeValueInfo: boolean =
    (attributeValue !== null && attributeValue !== "") ||
    (templateValue !== null && templateValue !== "") ||
    (editorExpression !== null && editorExpression !== "");

  const disabled = (isNewAttribute ? !isNewAttributeInfo : !isAttributeInfo) || !isAttributeValueInfo;

  const { alias, name, geometryAttribute, geometryType } = layer;

  const onChangeValue = useCallback(
    (inputValue: ChangeEvent<HTMLInputElement> | number) => {
      onChangeAttributeValue(inputValue);
    },
    [onChangeAttributeValue],
  );

  const formHandlers = {
    onChangeAttribute,
    onChangeAttributeValue: onChangeValue,
    onChangeAttributeName,
    onChangeAttributeType,
    handleSetTemplateValue,
    handleSetExpression,
  };

  return (
    <ModalDialog maxWidth={"50.5rem"} minWidth={"50.5rem"} minHeight={"31.5rem"}>
      <FeaturesMultiEditHeader layerName={alias || name} advancedMode={advancedMode} toggleMode={toggleMode} />
      <CalculationBlock
        layerName={name}
        isNewAttribute={isNewAttribute}
        attributeInfo={attributeInfo}
        advancedMode={advancedMode}
        formHandlers={formHandlers}
        editorExpression={editorExpression}
      />
      {!advancedMode && (
        <CalculationTemplates
          layerName={layer.name}
          handleSetTemplateValue={handleSetTemplateValue}
          toggleMode={toggleMode}
          geometryAttribute={geometryAttribute}
          geometryType={geometryType as GeometryType}
          available={!!attributeOption?.value}
        />
      )}
      <FeaturesMultiEditActions apply={runEditAttributesTask} wait={loadingLayer} disabled={disabled} />
    </ModalDialog>
  );
};
